import { Button, FormField, Select, Surface } from '@chopchop-se/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReceiptDetail, ReceivingScanResult } from '@warehouse/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { usePermission } from '../../../auth/auth-store';
import { toastError, toastInfo, toastSuccess } from '../../../lib/notify';
import { receiptsApi, receivingApi, receivingKeys } from '../api';
import { ScanField } from '../components/ScanField';
import { ScanResultCard } from '../components/ScanResultCard';
import { pickPoLine, scanAction, suggestedQty, suggestedUnitId } from '../format';

type PalletMode = 'none' | 'new' | 'sscc' | 'existing';

/**
 * The scan-to-receive panel (spec §5.7).
 *
 * Scan anything → the server resolves it → the form pre-fills with the article,
 * its PO line, the lot and best-before from the label, and the quantity the
 * label declared. The operator confirms. Everything on this screen is a
 * suggestion except what they type: what gets recorded is what was actually
 * scanned and confirmed, never what the system assumed.
 */
export function ReceivePanel({ receipt }: { receipt: ReceiptDetail }): ReactNode {
  const { t } = useTranslation('receiving');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canTeach = usePermission('warehouse.barcodes.teach');

  const [scan, setScan] = useState<ReceivingScanResult | null>(null);
  const [poLineId, setPoLineId] = useState<string>('');
  const [qty, setQty] = useState('');
  const [unitId, setUnitId] = useState<string | null>(null);
  const [lotNo, setLotNo] = useState('');
  const [bestBefore, setBestBefore] = useState('');
  const [palletMode, setPalletMode] = useState<PalletMode>('none');
  const [palletId, setPalletId] = useState<string | null>(null);
  const [sscc, setSscc] = useState<string | null>(null);
  const [lastLineAt, setLastLineAt] = useState(0);

  const closed = receipt.status === 'closed';
  const article = scan?.article ?? null;

  const resolve = useMutation({
    mutationFn: (code: string) => receivingApi.scan({ code, receiptId: receipt.id }),
    onSuccess: (result) => {
      setScan(result);
      applyScan(result);
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const addLine = useMutation({
    mutationFn: () =>
      receiptsApi.addLine(receipt.id, {
        poLineId: poLineId || null,
        articleId: article!.articleId,
        qty,
        unitId: unitId ?? article!.baseUnitId,
        lotNo: lotNo.trim() || null,
        bestBefore: bestBefore || null,
        palletId: palletMode === 'existing' ? palletId : null,
        ssccAlias: palletMode === 'sscc' ? sscc : null,
        newPallet: palletMode === 'new',
        scannedBarcode: scan?.data ?? null,
      }),
    onSuccess: async (result) => {
      toastSuccess(t('session.lineAdded'));
      if (result.bestBeforeMismatch) {
        toastInfo(t('line.bestBeforeMismatch', { date: result.bestBefore ?? '' }));
      }
      resetForm();
      setLastLineAt(Date.now());
      await queryClient.invalidateQueries({ queryKey: receivingKeys.receipt(receipt.id) });
    },
    onError: (error) => {
      toastError(error);
    },
  });

  /** Map a scan onto the form. One place, so every branch behaves the same. */
  function applyScan(result: ReceivingScanResult): void {
    const action = scanAction(result);

    if (action.kind === 'receive') {
      const line = pickPoLine(receipt.poLines, action.articleId);
      setPoLineId(line?.id ?? '');
      setQty(suggestedQty(result));
      setUnitId(suggestedUnitId(result));
      setLotNo(result.lotNo ?? '');
      setBestBefore(result.bestBefore ?? '');
      // The label's own pallet identity wins over whatever was selected before.
      if (result.sscc) {
        setSscc(result.sscc);
        setPalletMode('sscc');
      }
      return;
    }

    if (action.kind === 'pallet') {
      setPalletId(action.palletId);
      setPalletMode('existing');
      return;
    }

    if (action.kind === 'sscc') {
      setSscc(action.sscc);
      setPalletMode('sscc');
    }
  }

  function resetForm(): void {
    setScan(null);
    setPoLineId('');
    setQty('');
    setUnitId(null);
    setLotNo('');
    setBestBefore('');
    setPalletMode('none');
    setPalletId(null);
    setSscc(null);
  }

  // A closed session must not keep a half-filled form around.
  useEffect(() => {
    if (closed) resetForm();
  }, [closed]);

  const qtyValid = qty.trim().length > 0 && Number(qty) > 0;
  const lotMissing = Boolean(article?.lotTracked) && lotNo.trim().length === 0;

  const palletOptions = [
    { value: 'none', label: t('line.palletNone') },
    { value: 'new', label: t('line.palletNew') },
    ...(sscc ? [{ value: 'sscc', label: t('line.palletSscc', { sscc }) }] : []),
    ...(scan?.pallet ? [{ value: 'existing', label: t('line.palletExisting', { lpn: scan.pallet.lpn }) }] : []),
  ];

  return (
    <div className="space-y-4">
      <ScanField
        label={t('scan.label')}
        placeholder={t('scan.placeholder')}
        hint={t('scan.hint')}
        disabled={closed}
        busy={resolve.isPending}
        refocusKey={lastLineAt}
        onScan={(code) => {
          resolve.mutate(code);
        }}
        submitLabel={t('scan.label')}
      />

      {scan ? (
        <ScanResultCard
          scan={scan}
          canTeach={canTeach}
          onTeach={(code) => {
            void navigate(`/warehouse/teach-barcodes?code=${encodeURIComponent(code)}`);
          }}
        />
      ) : null}

      {article ? (
        <Surface className="space-y-4 p-4">
          <h3 className="text-sm font-medium text-ink">{t('line.title')}</h3>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Select
              label={t('line.poLine')}
              options={[
                { value: '', label: t('scan.noPoLine') },
                ...receipt.poLines
                  .filter((line) => line.articleId === article.articleId)
                  .map((line) => ({
                    value: line.id,
                    label: `${line.articleNo} · ${t('session.receivedOf', {
                      received: line.receivedQtyBase,
                      ordered: line.qtyBase,
                    })}`,
                  })),
              ]}
              value={poLineId}
              onChange={(event) => {
                setPoLineId(event.target.value);
              }}
            />

            <FormField
              label={t('line.qty')}
              type="number"
              min="0"
              step="0.001"
              inputMode="decimal"
              value={qty}
              onChange={(event) => {
                setQty(event.target.value);
              }}
              {...(qty.length > 0 && !qtyValid ? { error: t('line.qtyRequired') } : {})}
            />

            <Select
              label={t('line.unit')}
              options={[
                { value: article.baseUnitId, label: article.baseUnitCode },
                ...(article.unitId && article.unitId !== article.baseUnitId
                  ? [{ value: article.unitId, label: article.unitCode ?? '' }]
                  : []),
              ]}
              value={unitId ?? article.baseUnitId}
              onChange={(event) => {
                setUnitId(event.target.value);
              }}
            />

            <FormField
              label={t('line.lot')}
              placeholder={t('line.lotPlaceholder')}
              value={lotNo}
              onChange={(event) => {
                setLotNo(event.target.value);
              }}
              {...(lotMissing ? { error: t('line.lotRequired') } : {})}
            />

            <FormField
              label={t('line.bestBefore')}
              type="date"
              value={bestBefore}
              onChange={(event) => {
                setBestBefore(event.target.value);
              }}
            />

            <Select
              label={t('line.pallet')}
              options={palletOptions}
              value={palletMode}
              onChange={(event) => {
                setPalletMode(event.target.value as PalletMode);
              }}
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-ink-muted">
              {t('line.bin')}: {t('line.binDefault')}
            </p>
            <Button
              loading={addLine.isPending}
              disabled={closed || !qtyValid || lotMissing}
              onClick={() => {
                addLine.mutate();
              }}
            >
              {addLine.isPending ? t('line.adding') : t('line.add')}
            </Button>
          </div>
        </Surface>
      ) : null}
    </div>
  );
}
